import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpRequest
} from '@angular/common/http'
import { inject } from '@angular/core'
import { Router } from '@angular/router'
import { Actions, ofType } from '@ngrx/effects'
import { Store } from '@ngrx/store'
import {
  catchError,
  finalize,
  map,
  Observable,
  of,
  shareReplay,
  switchMap,
  take,
  throwError,
  timeout
} from 'rxjs'
import { AppRoutesNS } from 'src/app/app-routes.namespace'
import { environment } from 'src/environments/environment'
import { authActions } from '../auth/auth.actions'
import { authSelectors } from '../auth/auth.selectors'
import { TokenResponse } from '../auth/interfaces'
import { AppState } from '../core.state'

export const TOKEN_REFRESH_TIMEOUT_MS = 15000

const TOKEN_REFRESH_ENDPOINT = '/auth/token-refresh'

const TOKEN_EXPIRY_BUFFER_SECONDS = 5 * 60

const PAGES_WITHOUT_TOKEN_RETRY = [
  AppRoutesNS.Pages.LOGIN,
  AppRoutesNS.OIDC.AUTH_CALLBACK
]

export type AuthRetryDeps = {
  store: Store<AppState>
  actions$: Actions
  router: Router
}

let refreshResult$: Observable<boolean> | null = null

export function resetAuthRetryState(): void {
  refreshResult$ = null
}

export function AuthRetryInterceptor(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> {
  const deps: AuthRetryDeps = {
    store: inject(Store<AppState>),
    actions$: inject(Actions),
    router: inject(Router)
  }

  return createAuthRetryHandler(deps)(req, next)
}

export function createAuthRetryHandler(deps: AuthRetryDeps) {
  return (
    req: HttpRequest<unknown>,
    next: HttpHandlerFn
  ): Observable<HttpEvent<unknown>> =>
    next(req).pipe(
      catchError((error: unknown) => {
        if (!isRecoverableUnauthorized(req, error)) {
          return throwError(() => error)
        }

        return deps.store.select(authSelectors.selectTokenResponse).pipe(
          take(1),
          switchMap((tokenResponse) =>
            handleUnauthorized(deps, req, next, error, tokenResponse)
          )
        )
      })
    )
}

function isRecoverableUnauthorized(
  req: HttpRequest<unknown>,
  error: unknown
): boolean {
  return (
    error instanceof HttpErrorResponse &&
    error.status === 401 &&
    req.url.includes(environment.backendApiUrl) &&
    !req.url.includes(TOKEN_REFRESH_ENDPOINT)
  )
}

function isAccessTokenExpired(accessToken: string | undefined): boolean {
  if (!accessToken?.length) {
    return true
  }

  try {
    const payload = JSON.parse(atob(accessToken.split('.')[1]))
    const exp = payload?.exp

    if (typeof exp !== 'number') {
      return true
    }

    const nowSeconds = Math.floor(Date.now() / 1000)
    return exp - nowSeconds <= TOKEN_EXPIRY_BUFFER_SECONDS
  } catch {
    return true
  }
}

function handleUnauthorized(
  deps: AuthRetryDeps,
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
  error: unknown,
  tokenResponse: TokenResponse | null
): Observable<HttpEvent<unknown>> {
  if (!tokenResponse?.refreshToken || isOnPageWithoutTokenRetry(deps.router)) {
    // no replay possible: let the auth effects decide (refresh/logout/noop)
    deps.store.dispatch(authActions.onUnauthorizedTokenRefresh())
    return throwError(() => error)
  }

  if (!refreshResult$ && !isAccessTokenExpired(tokenResponse.accessToken)) {
    return throwError(() => error)
  }

  return refreshTokenOnce(deps).pipe(
    switchMap((didRefresh) =>
      didRefresh
        ? replayWithFreshToken(deps, req, next, error)
        : throwError(() => error)
    )
  )
}

function refreshTokenOnce(deps: AuthRetryDeps): Observable<boolean> {
  if (refreshResult$) {
    return refreshResult$
  }

  refreshResult$ = deps.actions$.pipe(
    ofType(authActions.authenticationSuccess, authActions.logout),
    take(1),
    map((action) => action.type === authActions.authenticationSuccess.type),
    timeout({ first: TOKEN_REFRESH_TIMEOUT_MS, with: () => of(false) }),
    finalize(() => {
      refreshResult$ = null
    }),
    shareReplay({ bufferSize: 1, refCount: false })
  )

  refreshResult$.pipe(take(1)).subscribe()
  deps.store.dispatch(authActions.onUnauthorizedTokenRefresh())

  return refreshResult$
}

function replayWithFreshToken(
  deps: AuthRetryDeps,
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
  originalError: unknown
): Observable<HttpEvent<unknown>> {
  return deps.store.select(authSelectors.selectToken).pipe(
    take(1),
    switchMap((token) => {
      if (!token) {
        return throwError(() => originalError)
      }

      return next(
        req.clone({
          setHeaders: {
            Authorization: 'Bearer ' + token
          }
        })
      )
    })
  )
}

function isOnPageWithoutTokenRetry(router: Router): boolean {
  return PAGES_WITHOUT_TOKEN_RETRY.some((page) => router.url?.includes(page))
}
